// src/pages/Matriculas/ComprarMatricula.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Steps, Spin, Result, Button, Alert } from 'antd';
import { 
  IdcardOutlined, 
  DollarOutlined, 
  CheckCircleOutlined,
  ArrowLeftOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons';

import InfoRecinto from './components/InfoRecinto';
import DetalleMatricula from './components/DetalleMatricula';
import ResumenCompra from './components/ResumenCompra';

import { useNotify } from '../../../providers/NotificationProvider';
import { useCart } from '../../../providers/Cart';
import Auth from '../../../service/Auth';
import MatriculaService from '../../../service/Matriculas';

const { Step } = Steps;

export default function ComprarMatricula() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const notify = useNotify();
  const { addToCart } = useCart();

  const recintoIdParam = searchParams.get('recinto');

  // Estados
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [recintos, setRecintos] = useState([]);
  const [recintoSeleccionado, setRecintoSeleccionado] = useState(null);
  const [configuracionMatricula, setConfiguracionMatricula] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [agregandoCarrito, setAgregandoCarrito] = useState(false);

  // Cargar datos iniciales
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // 1. Verificar autenticación
        const userData = await Auth.Me();
        setUser({
          sub: userData.id || userData.sub,
          name: userData.name || userData.dynamoProfile?.name,
          email: userData.email,
          rut: userData.rut || userData.dynamoProfile?.rut,
          phone: userData.phone || userData.dynamoProfile?.phone
        });

        // 2. Cargar recintos con matrícula ACTIVA (a la venta)
        const recintosConMatricula = await MatriculaService.ObtenerRecintosConMatricula();

        setRecintos(recintosConMatricula);

        // 3. Si viene recintoId en URL, seleccionarlo automáticamente
        if (recintoIdParam) {
          const recintoPreseleccionado = recintosConMatricula.find(
            r => r._id === recintoIdParam
          );

          if (recintoPreseleccionado) {
            setRecintoSeleccionado(recintoPreseleccionado);
            setConfiguracionMatricula(recintoPreseleccionado.configuracionMatricula);
            setCurrentStep(1); // Ir directo al paso 2
          }
        }

      } catch (error) {
        console.error('Error cargando datos:', error);
        notify.error('Error al cargar información. Intenta nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [recintoIdParam]);

  // Manejar selección de recinto
  const handleSeleccionarRecinto = (recinto) => {
    setRecintoSeleccionado(recinto);
    setConfiguracionMatricula(recinto.configuracionMatricula);
    setCurrentStep(1);
  };

  // Agregar al carrito
  const handleAgregarAlCarrito = async () => {
    try {
      setAgregandoCarrito(true);

      const itemData = {
        tipo: 'matricula',
        recintoId: recintoSeleccionado._id,
        recintoNombre: recintoSeleccionado.nombre,
        usuarioData: {
          cognitoId: user.sub,
          nombre: user.name,
          email: user.email,
          rut: user.rut,
          telefono: user.phone
        },
        configuracionMatricula: {
          _id: configuracionMatricula._id,
          valor: configuracionMatricula.valorMatricula,
          tipoVigencia: configuracionMatricula.tipoVigencia,
          mesesVigencia: configuracionMatricula.mesesVigencia,
          descripcion: configuracionMatricula.descripcion
        },
        precio: configuracionMatricula.valorMatricula
      };

      await addToCart(itemData);
      notify.success('Matrícula agregada al carrito exitosamente');
      navigate('/carrito');

    } catch (error) {
      console.error('Error agregando al carrito:', error);
      notify.error(error.message || 'Error al agregar matrícula al carrito');
    } finally {
      setAgregandoCarrito(false);
    }
  };

  // Loading inicial
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spin size="large" tip="Cargando información..." />
      </div>
    );
  }

  // Sin usuario
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Result
          status="403"
          title="Debes iniciar sesión"
          subTitle="Para comprar una matrícula, necesitas estar autenticado"
          extra={
            <Button type="primary" onClick={() => navigate('/login')}>
              Iniciar Sesión
            </Button>
          }
        />
      </div>
    );
  }

  // Sin recintos con matrícula
  if (recintos.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Result
          status="404"
          title="No hay matrículas disponibles"
          subTitle="Actualmente no hay recintos con matrícula configurada"
          extra={
            <Button type="primary" onClick={() => navigate('/talleres')}>
              Ver Talleres
            </Button>
          }
        />
      </div>
    );
  }

  const steps = [
    {
      title: 'Seleccionar Recinto',
      icon: <IdcardOutlined />
    },
    {
      title: 'Detalle de Matrícula',
      icon: <DollarOutlined />
    },
    {
      title: 'Confirmar Compra',
      icon: <CheckCircleOutlined />
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            Volver
          </Button>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Comprar Matrícula
          </h1>
          <p className="text-gray-600">
            Adquiere tu matrícula para acceder a talleres exclusivos
          </p>
        </div>

        {/* Steps */}
        <Card className="mb-6">
          <Steps current={currentStep} items={steps} />
        </Card>

        {/* Info del usuario */}
        <Alert
          message={`Comprando para: ${user.name}`}
          description={
            <div className="text-sm">
              <p>Email: {user.email}</p>
              {user.rut && <p>RUT: {user.rut}</p>}
            </div>
          }
          type="info"
          showIcon
          className="mb-6"
        />

        {/* Contenido según paso */}
        {currentStep === 0 && (
          <InfoRecinto
            recintos={recintos}
            recintoSeleccionado={recintoSeleccionado}
            onSeleccionar={handleSeleccionarRecinto}
          />
        )}

        {currentStep === 1 && recintoSeleccionado && (
          <DetalleMatricula
            recinto={recintoSeleccionado}
            configuracion={configuracionMatricula}
            onContinuar={() => setCurrentStep(2)}
            onVolver={() => setCurrentStep(0)}
          />
        )}

        {currentStep === 2 && recintoSeleccionado && (
          <ResumenCompra
            user={user}
            recinto={recintoSeleccionado}
            configuracion={configuracionMatricula}
            loading={agregandoCarrito}
            onConfirmar={handleAgregarAlCarrito}
            onVolver={() => setCurrentStep(1)}
          />
        )}
      </div>
    </div>
  );
}
